import CrossIcon from '@images/icons/cross-button-icon.svg?react'
import cn from 'classnames'
import { useModal } from 'mmfintech-commons'
import { type BaseSyntheticEvent, useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import type { CoreModalDialogProps } from './CoreModalDialog.types'

import './modalDialog.scss'

let openModalsCount = 0

export const CoreModalDialog = ({
  header,
  content,
  options = null,
  visible,
  onClose = undefined
}: CoreModalDialogProps) => {
  const { ref, modalOptions, clickOutsideRef } = useModal({
    options,
    visible,
    onClose
  })

  const location = useLocation()

  const handleClose = useCallback(() => {
    onClose?.()
  }, [onClose])

  const stopPropagation = (e: BaseSyntheticEvent) => {
    e.stopPropagation()
  }

  useEffect(() => {
    if (visible) {
      handleClose()
    }
  }, [location.pathname])

  // Own class on purpose: useModal toggles `modal-opened` on every render and leaves it removed while open.
  // Counted because several dialogs can be mounted at once, and the last one to close releases the lock.
  useEffect(() => {
    if (!visible) return
    openModalsCount += 1
    document.body.classList.add('modal-scroll-locked')
    return () => {
      openModalsCount -= 1
      if (openModalsCount === 0) document.body.classList.remove('modal-scroll-locked')
    }
  }, [visible])

  return visible ? (
    <div className='modal-wrapper' ref={ref}>
      <div
        ref={clickOutsideRef}
        data-test='modal-container'
        className={`modal-container ${cn({
          'size-auto': modalOptions.size === 'auto',
          'size-large': modalOptions.size === 'large',
          'size-small': modalOptions.size === 'small',
          'size-extra-large': modalOptions.size === 'extra-large',
          centered: modalOptions.size === 'centered',
          horizontal: modalOptions.size === 'horizontal',
          transparent: modalOptions.transparent === true,
          'overflow-none': modalOptions.overflow === 'none',
          'overflow-visible': modalOptions.overflow === 'visible'
        })}`}
        onClick={stopPropagation}
      >
        {(header || !options?.hideCloseIcon) && (
          <div className='header'>
            <div>{header}</div>
            <div className='header-buttons-wrapper'>
              {options?.additionalActions && options?.additionalActions}
              {!options?.hideCloseIcon && <CrossIcon className='close-icon' onClick={handleClose} />}
            </div>
          </div>
        )}
        <main className='modal-main'>{content}</main>
      </div>
    </div>
  ) : null
}
